// eslint-disable-next-line import/no-extraneous-dependencies
import { jsPDF } from 'jspdf';
import { Asset, AssetType, PassTemplate } from '../../models/PassTemplate';
import { BackgroundSizeType } from '../../models/enums/PassTemplates/BackgroundSizeType';
import { BackgroundType } from '../../models/enums/PassTemplates/BackgroundType';
import { OrientationType } from '../../models/enums/PassTemplates/OrientationType';
import { PassTemplateType } from '../../models/enums/PassTemplates/PassTemplateType';
import { PassTemplatePerson } from '../../models/PassTemplates/PassTemplatePerson';
import { usedFonts } from './fonts';

export const generatePDF = async (passTemplate: PassTemplate, person?: PassTemplatePerson) => {
  let pdfFormat;
  if (passTemplate.Type === PassTemplateType.Slim) {
    pdfFormat = passTemplate.Orientation === OrientationType.Landscape ? [400, 718] : [718, 400];
  } else if (passTemplate.Type === PassTemplateType.Square) {
    pdfFormat = [398, 398];
  } else {
    pdfFormat = passTemplate.Orientation === OrientationType.Landscape ? [398, 616.172] : [616.172, 398];
  }

  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({
    orientation: passTemplate.Orientation === OrientationType.Landscape ? 'landscape' : 'portrait',
    unit: 'px',
    format: pdfFormat,
  });

  // Set background
  if (passTemplate.BackgroundType === BackgroundType.Color) {
    pdf.setFillColor(passTemplate.BackgroundColor);
    pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F');
  } else if (passTemplate.BackgroundType === BackgroundType.Image && passTemplate.BackgroundImage?.Url) {
    try {
      await addBackgroundImageToPdf(
        pdf,
        passTemplate.BackgroundImage.Url,
        passTemplate.BackgroundImage.MimeType,
        passTemplate.BackgroundSize
      );
    } catch (error) {
      console.error(error);
    }
  }

  // Sort assets on Layer (JsPDF doesn't support z-index)
  const sortedAssets = [...passTemplate.Assets].sort((a, b) => a.Layer - b.Layer);

  // Add assets to the PDF
  try {
    // eslint complains about using a for loop, but we have no choice.
    // eslint-disable-next-line no-restricted-syntax
    for (const element of sortedAssets) {
      const asset = element;
      // eslint complains that we shouldn't await in for loops, which is valid, but if we don't the layering will not be applied.
      // eslint-disable-next-line no-await-in-loop
      await addAssetToPdf(pdf, asset, person);
    }
  } catch (error) {
    console.error(error);
  }

  pdf.save(`${passTemplate.Name || 'PassTemplate'}.pdf`);
};

const addBackgroundImageToPdf = async (
  pdf: jsPDF,
  imageUrl: string,
  mimeType: string | undefined,
  backgroundSize: BackgroundSizeType
): Promise<void> => {
  try {
    const img = await loadImage(imageUrl);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = img.width;
    const imgHeight = img.height;

    let drawWidth = pageWidth;
    let drawHeight = pageHeight;
    let x = 0;
    let y = 0;

    if (backgroundSize === BackgroundSizeType.Contain) {
      const scale = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
      drawWidth = imgWidth * scale;
      drawHeight = imgHeight * scale;
      x = (pageWidth - drawWidth) / 2;
      y = (pageHeight - drawHeight) / 2;
    } else if (backgroundSize === BackgroundSizeType.Cover) {
      const scale = Math.max(pageWidth / imgWidth, pageHeight / imgHeight);
      drawWidth = imgWidth * scale;
      drawHeight = imgHeight * scale;
      x = (pageWidth - drawWidth) / 2;
      y = (pageHeight - drawHeight) / 2;
    }

    pdf.addImage(img, mimeType || 'JPEG', x, y, drawWidth, drawHeight);
  } catch (error) {
    console.error(error);
  }
};

const addAssetToPdf = async (pdf: jsPDF, asset: Asset, person?: PassTemplatePerson): Promise<void> => {
  let opacity = asset.Opacity ?? 1;
  // Clamp opacity to 0-1
  opacity = Math.min(1, Math.max(0, opacity > 1 ? opacity / 100 : opacity));

  pdf.saveGraphicsState();
  pdf.setGState(pdf.GState({ opacity }));

  const assetHandlers = {
    [AssetType.FirstName]: () => person && drawText(pdf, asset, person.FirstName),
    [AssetType.LastName]: () => person && drawText(pdf, asset, person.LastName),
    [AssetType.Email]: () => person && drawText(pdf, asset, person.Email),
    [AssetType.Department]: () => person?.Department && drawText(pdf, asset, person.Department),
    [AssetType.Text]: () => drawText(pdf, asset, asset.Text),
    [AssetType.Picture]: async () => {
      const updatedAsset: Asset = { ...asset, Image: { Url: person?.Picture } };

      await addImageToPdf(pdf, updatedAsset);
    },
    [AssetType.Image]: async () => addImageToPdf(pdf, asset),
  };

  if (assetHandlers[asset.Type]) {
    await assetHandlers[asset.Type]();
  }
};

const drawText = (pdf: jsPDF, asset: Asset, text: string) => {
  // For some reason there is a difference between how the browser and jsPDF render text.
  // It looks like the font is about 33% bigger in the browser than jsPDF.
  // This number might be off, since I'm eyeballing it,
  // but it's close enough that the difference isn't noticable.
  const fontSize = (asset.FontSize || 12) * 1.33;
  const font: string =
    usedFonts.find((item) => item.name.toLowerCase() === asset.Font.toLowerCase())?.jspdfName || 'helvetica';
  pdf.setFont(font);
  pdf.setFontSize(fontSize);
  pdf.setTextColor(asset.FontColor || '#000');

  const { width } = pdf.internal.pageSize;
  const dim = pdf.getTextDimensions(text);
  const textWidth = dim.w;
  const textHeight = dim.h;

  const adjustedX = Math.min(asset.X, width - textWidth);
  const adjustedY = asset.Y + textHeight;

  pdf.text(text, adjustedX, adjustedY, { angle: asset.Rotation || 0 });
  pdf.restoreGraphicsState();
};

const getBorderRadiusData = (size: number, borderRadius: number) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const radius = (borderRadius / 100) * size;

  if (!ctx) {
    throw new Error('Canvas context is not supported');
  }

  // Set canvas size to match image dimensions
  canvas.width = size;
  canvas.height = size;

  // Create a rounded rectangle clip path
  ctx.beginPath();

  ctx.moveTo(0, 0);
  ctx.lineTo(canvas.width / 2, 0);
  ctx.arcTo(canvas.width, 0, canvas.width, canvas.height / 2, radius);
  ctx.arcTo(canvas.width, canvas.height, canvas.width / 2, canvas.height, radius);
  ctx.arcTo(0, canvas.height, 0, canvas.height / 2, radius);
  ctx.arcTo(0, 0, canvas.width / 2, 0, radius);

  ctx.closePath();
  ctx.clip();

  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  return ctx.getImageData(0, 0, canvas.width, canvas.height);
};

const addImageToPdf = async (pdf: jsPDF, asset: Asset) => {
  try {
    if (!asset.Image?.Url) {
      throw new Error('Image URL is missing');
    }

    const img = await loadImage(asset.Image.Url);

    // If borderRadius is 0, directly add the image without extra processing
    if (asset.BorderRadius <= 0) {
      pdf.addImage(
        img,
        asset.Image.MimeType || 'JPEG',
        asset.X,
        asset.Y,
        asset.Width,
        asset.Height,
        '',
        undefined,
        asset.Rotation || 0
      );
      return;
    }

    // Create a canvas to apply rounded corners
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Canvas context is not supported');
    }

    // Set canvas size to match image dimensions
    const scaleFactor = 2;
    canvas.width = asset.Width * scaleFactor;
    canvas.height = asset.Height * scaleFactor;

    // Draw the image within the clipped area
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const size = canvas.width;
    const radiusData = getBorderRadiusData(size, asset.BorderRadius);
    const drawnImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let x = 0; x < canvas.width; x++) {
      for (let y = 0; y < canvas.height; y++) {
        const aX = Math.floor((x * size) / canvas.width);
        const aY = Math.floor((y * size) / canvas.height);

        drawnImageData.data[(canvas.width * y + x) * 4 + 3] = radiusData.data[(size * aY + aX) * 4 + 3];
      }
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.putImageData(drawnImageData, 0, 0);

    // Convert the canvas to a Base64 image
    const imgData = canvas.toDataURL('image/png', 1.0);

    // Add the processed image to the PDF
    pdf.addImage(imgData, 'PNG', asset.X, asset.Y, asset.Width, asset.Height, '', undefined, asset.Rotation || 0);
  } catch (error) {
    console.error('Error adding image to PDF:', error);
  }
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.setAttribute('crossorigin', 'anonymous');
    img.src = src;
  });
};
